const LS_LANG = 'mingo_language';
const LS_LANG_ALT = 'language';
const LS_REGIONS = 'mingo_regions';
const LS_REGIONS_ALT = 'selectedRegions';
const LS_GPS_CACHE = 'mingo_gps_cache';
const LS_INTEREST_PICK = 'mingo_interest_pick';
const LS_ACTIVE_SOURCE = 'mingo_active_source';
const LS_SETUP = 'mingo_setup_complete';
const LS_SESSION = 'mingo_session';
const LS_LOGGED_IN = 'mingo_is_logged_in';
const LS_GUEST = 'mingo_is_guest';
const LS_SIGNUP_FLOW = 'mingo_signup_flow';
const LS_NICKNAME = 'mingo_nickname';
const LS_NATIONALITY = 'mingo_nationality';

/** 메인 앱(탭) 진입: 로그인 또는 둘러보기 게스트 */
export function canEnterMainApp() {
  if (typeof window === 'undefined') return false;
  if (window.localStorage.getItem(LS_GUEST) === 'true') return true;
  if (window.localStorage.getItem(LS_LOGGED_IN) === 'true') return true;
  // setup 완료(둘러보기·온보딩 마침)만으로 진입 허용 — logged_in 플래그는 세션 있을 때만
  if (window.localStorage.getItem(LS_SETUP) === '1') return true;
  return false;
}

/** UI language saved (onboarding step 1 done; region may come from GPS or profile later). */
export function hasLocalePreferences() {
  if (typeof window === 'undefined') return false;
  return Boolean(loadLanguage());
}

export function isLoggedInStorage() {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(LS_LOGGED_IN) === 'true';
}

export function isGuestStorage() {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(LS_GUEST) === 'true';
}

export function isSignupFlowActive() {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(LS_SIGNUP_FLOW) === '1';
}

export function isSetupCompleteStorage() {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(LS_SETUP) === '1';
}

export function beginSignupFlow() {
  window.localStorage.setItem(LS_SIGNUP_FLOW, '1');
}

/** 온보딩 미리보기: 홈 리다이렉트 플래그만 해제하고 가입 플로우 시작 */
export function restartOnboardingFlow() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(LS_SETUP);
  window.localStorage.removeItem(LS_LOGGED_IN);
  window.localStorage.removeItem(LS_GUEST);
  beginSignupFlow();
}

export function endSignupFlow() {
  window.localStorage.removeItem(LS_SIGNUP_FLOW);
}

export function setLoggedInTrue() {
  window.localStorage.setItem(LS_LOGGED_IN, 'true');
  window.localStorage.removeItem(LS_GUEST);
  endSignupFlow();
}

export function setGuestMode() {
  window.localStorage.setItem(LS_GUEST, 'true');
  window.localStorage.removeItem(LS_LOGGED_IN);
  endSignupFlow();
}

export function setSetupComplete() {
  window.localStorage.setItem(LS_SETUP, '1');
}

export function saveSession(payload = '1') {
  window.localStorage.setItem(LS_SESSION, typeof payload === 'string' ? payload : '1');
}

export function loadSession() {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(LS_SESSION);
}

export function clearSession() {
  window.localStorage.removeItem(LS_SESSION);
}

export function saveNickname(name) {
  window.localStorage.setItem(LS_NICKNAME, name);
}

export function loadNickname() {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem(LS_NICKNAME) || '';
}

export function loadLanguage() {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(LS_LANG) || window.localStorage.getItem(LS_LANG_ALT);
}

export function saveLanguage(code) {
  window.localStorage.setItem(LS_LANG, code);
}

export function loadRegions() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(LS_REGIONS) || window.localStorage.getItem(LS_REGIONS_ALT);
    if (!raw) return null;
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || arr.length === 0) return null;
    return arr.slice(0, 2);
  } catch {
    return null;
  }
}

export function saveRegions(regions) {
  const next = Array.isArray(regions) ? regions.slice(0, 2) : [];
  window.localStorage.setItem(LS_REGIONS, JSON.stringify(next));
}

/** @returns {{ coords?: { lat: number; lng: number } | null; location?: object | null; label?: string } | null} */
export function loadGpsCache() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(LS_GPS_CACHE);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return data && typeof data === 'object' ? data : null;
  } catch {
    return null;
  }
}

/** @param {{ coords?: { lat: number; lng: number } | null; location?: object | null; label?: string }} payload */
export function saveGpsCache(payload) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LS_GPS_CACHE, JSON.stringify(payload || {}));
  } catch {
    /* ignore */
  }
}

/** @returns {import('./userLocationState').LocationHierarchy | null} */
export function loadInterestPick() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(LS_INTEREST_PICK);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return data && typeof data === 'object' ? data : null;
  } catch {
    return null;
  }
}

/** @param {import('./userLocationState').LocationHierarchy | null} pick */
export function saveInterestPick(pick) {
  if (typeof window === 'undefined') return;
  try {
    if (!pick) {
      window.localStorage.removeItem(LS_INTEREST_PICK);
      return;
    }
    window.localStorage.setItem(LS_INTEREST_PICK, JSON.stringify(pick));
  } catch {
    /* ignore */
  }
}

/** @returns {'gps' | 'interest' | null} */
export function loadActiveSource() {
  if (typeof window === 'undefined') return null;
  const v = window.localStorage.getItem(LS_ACTIVE_SOURCE);
  return v === 'interest' ? 'interest' : v === 'gps' ? 'gps' : null;
}

/** @param {'gps' | 'interest'} source */
export function saveActiveSource(source) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LS_ACTIVE_SOURCE, source === 'interest' ? 'interest' : 'gps');
}

export function getNationality() {
  try {
    return window.localStorage.getItem(LS_NATIONALITY) ?? 'KR';
  } catch {
    return 'KR';
  }
}

export function saveNationality(code) {
  try {
    if (code == null || String(code).trim() === '') return;
    window.localStorage.setItem(LS_NATIONALITY, String(code).trim());
  } catch {
    /* ignore */
  }
}

/** Supabase 세션 만료 등 — 로그인 플래그만 제거 (언어·지역 유지) */
export function clearAuthSessionFlags() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(LS_LOGGED_IN);
  window.localStorage.removeItem(LS_SESSION);
}

/** 로그아웃: 로컬스토리지 초기화 */
export function clearAllUserData() {
  if (typeof window === 'undefined') return;
  [
    LS_LANG,
    LS_REGIONS,
    LS_GPS_CACHE,
    LS_INTEREST_PICK,
    LS_ACTIVE_SOURCE,
    LS_SETUP,
    LS_SESSION,
    LS_LOGGED_IN,
    LS_GUEST,
    LS_SIGNUP_FLOW,
    LS_NICKNAME,
    LS_NATIONALITY,
  ].forEach((k) => window.localStorage.removeItem(k));
}
